import { create } from 'zustand';
import api from '@/services/api';
import { identityCheck } from '@/features/a0/identity/identity';
import type {
  BatchSummary,
  BatchDetail,
  NeedsMappingResponse,
  RowUpdate,
  ConfirmMappingRequest,
  AnalyticsPlatform,
  MetricRow,
} from '@/types';

interface AnalyticsStore {
  // State
  batches: BatchSummary[];
  currentBatch: BatchDetail | null;
  pendingMapping: NeedsMappingResponse | null; // set when an upload needs header mapping
  isLoading: boolean;
  isUploading: boolean;
  error: string | null;

  // Actions
  fetchBatches: (workspaceId: string) => Promise<void>;
  fetchBatch: (workspaceId: string, batchId: string) => Promise<void>;
  uploadFile: (workspaceId: string, file: File, platform: AnalyticsPlatform) => Promise<'batch' | 'mapping' | null>;
  uploadScreenshot: (workspaceId: string, file: File, platform: AnalyticsPlatform) => Promise<boolean>;
  confirmMapping: (workspaceId: string, data: ConfirmMappingRequest) => Promise<BatchDetail | null>;
  updateRow: (workspaceId: string, batchId: string, rowId: string, data: RowUpdate) => Promise<void>;
  deleteRow: (workspaceId: string, batchId: string, rowId: string) => Promise<void>;
  confirmBatch: (workspaceId: string, batchId: string, timezone: string) => Promise<boolean>;
  discardBatch: (workspaceId: string, batchId: string) => Promise<boolean>;
  clearCurrentBatch: () => void;
  clearPendingMapping: () => void;
  clearError: () => void;
}

function extractError(err: any, fallback: string): string {
  const status = err?.response?.status;
  if (status === 413) return 'File is too large.';
  if (status === 409) return 'This batch is no longer pending review — refreshing.';
  return err?.response?.data?.detail || fallback;
}

export const useAnalyticsStore = create<AnalyticsStore>((set, get) => ({
  batches: [],
  currentBatch: null,
  pendingMapping: null,
  isLoading: false,
  isUploading: false,
  error: null,

  fetchBatches: async (workspaceId: string) => {
    const live = identityCheck();
    set({ isLoading: true, error: null });
    try {
      const batches = await api.getAnalyticsBatches(workspaceId);
      if (!live()) return;
      set({ batches, isLoading: false });
    } catch (err: any) {
      if (!live()) return;
      set({ error: extractError(err, 'Failed to load import batches'), isLoading: false });
    }
  },

  fetchBatch: async (workspaceId: string, batchId: string) => {
    const live = identityCheck();
    set({ isLoading: true, error: null });
    try {
      const batch = await api.getAnalyticsBatch(workspaceId, batchId);
      if (!live()) return;
      set({ currentBatch: batch, isLoading: false });
    } catch (err: any) {
      if (!live()) return;
      set({ error: extractError(err, 'Failed to load batch'), isLoading: false });
    }
  },

  uploadFile: async (workspaceId: string, file: File, platform: AnalyticsPlatform) => {
    const live = identityCheck();
    set({ isUploading: true, error: null });
    try {
      const result = await api.uploadAnalyticsFile(workspaceId, file, platform);
      if (!live()) return null;
      if ('needs_mapping' in result && result.needs_mapping) {
        set({ pendingMapping: result, isUploading: false });
        return 'mapping';
      }
      const batch = result as BatchDetail;
      set({ currentBatch: batch, isUploading: false });
      await get().fetchBatches(workspaceId);
      return 'batch';
    } catch (err: any) {
      if (!live()) return null;
      set({ error: extractError(err, 'Failed to upload file'), isUploading: false });
      return null;
    }
  },

  uploadScreenshot: async (workspaceId: string, file: File, platform: AnalyticsPlatform) => {
    const live = identityCheck();
    set({ isUploading: true, error: null });
    try {
      const batch = await api.uploadAnalyticsScreenshot(workspaceId, file, platform);
      if (!live()) return false;
      set({ currentBatch: batch, isUploading: false });
      await get().fetchBatches(workspaceId);
      return true;
    } catch (err: any) {
      if (!live()) return false;
      set({ error: extractError(err, 'Could not read this screenshot'), isUploading: false });
      return false;
    }
  },

  confirmMapping: async (workspaceId: string, data: ConfirmMappingRequest) => {
    const live = identityCheck();
    set({ isUploading: true, error: null });
    try {
      const batch = await api.confirmAnalyticsMapping(workspaceId, data);
      if (!live()) return null;
      set({ currentBatch: batch, pendingMapping: null, isUploading: false });
      await get().fetchBatches(workspaceId);
      return batch;
    } catch (err: any) {
      if (!live()) return null;
      set({ error: extractError(err, 'Failed to confirm mapping'), isUploading: false });
      return null;
    }
  },

  updateRow: async (workspaceId: string, batchId: string, rowId: string, data: RowUpdate) => {
    const live = identityCheck();
    try {
      const updated = await api.updateAnalyticsRow(workspaceId, batchId, rowId, data);
      if (!live()) return;
      const { currentBatch } = get();
      if (currentBatch && currentBatch.id === batchId) {
        set({
          currentBatch: {
            ...currentBatch,
            rows: currentBatch.rows.map((r: MetricRow) => (r.id === rowId ? updated : r)),
          },
        });
      }
    } catch (err: any) {
      if (!live()) throw err;
      set({ error: extractError(err, 'Failed to update row') });
      if (err?.response?.status === 409) await get().fetchBatch(workspaceId, batchId);
      throw err;
    }
  },

  deleteRow: async (workspaceId: string, batchId: string, rowId: string) => {
    const live = identityCheck();
    try {
      await api.deleteAnalyticsRow(workspaceId, batchId, rowId);
      if (!live()) return;
      const { currentBatch } = get();
      if (currentBatch && currentBatch.id === batchId) {
        set({
          currentBatch: {
            ...currentBatch,
            rows: currentBatch.rows.filter((r: MetricRow) => r.id !== rowId),
          },
        });
      }
    } catch (err: any) {
      if (!live()) throw err;
      set({ error: extractError(err, 'Failed to delete row') });
      if (err?.response?.status === 409) await get().fetchBatch(workspaceId, batchId);
      throw err;
    }
  },

  confirmBatch: async (workspaceId: string, batchId: string, timezone: string) => {
    const live = identityCheck();
    set({ isLoading: true, error: null });
    try {
      const batch = await api.confirmAnalyticsBatch(workspaceId, batchId, timezone);
      if (!live()) return false;
      set({ currentBatch: batch, isLoading: false });
      await get().fetchBatches(workspaceId);
      return true;
    } catch (err: any) {
      if (!live()) return false;
      set({ error: extractError(err, 'Failed to confirm batch'), isLoading: false });
      if (err?.response?.status === 409) await get().fetchBatch(workspaceId, batchId);
      return false;
    }
  },

  discardBatch: async (workspaceId: string, batchId: string) => {
    const live = identityCheck();
    set({ isLoading: true, error: null });
    try {
      await api.discardAnalyticsBatch(workspaceId, batchId);
      if (!live()) return false;
      const { currentBatch } = get();
      set({
        currentBatch: currentBatch && currentBatch.id === batchId ? null : currentBatch,
        isLoading: false,
      });
      await get().fetchBatches(workspaceId);
      return true;
    } catch (err: any) {
      if (!live()) return false;
      set({ error: extractError(err, 'Failed to discard batch'), isLoading: false });
      if (err?.response?.status === 409) await get().fetchBatch(workspaceId, batchId);
      return false;
    }
  },

  clearCurrentBatch: () => set({ currentBatch: null }),
  clearPendingMapping: () => set({ pendingMapping: null }),
  clearError: () => set({ error: null }),
}));
